import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { DESKTOP_PDP_VIDEO } from 'constants/appConstants';
import DesktopSwatchPicker from 'components/productdetail/DesktopSwatchPicker';
import ImageZoom from 'components/productdetail/ImageZoom';
import { Video as VideoIcon } from 'components/icons';
import ImageLazyLoader from 'components/common/ImageLazyLoader';
import MelodyVideoPlayer from 'components/common/melodyVideo/MelodyVideoPlayer';
import { constructMSAImageUrl } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';
import marketplace from 'cfg/marketplace.json';

import css from 'styles/components/productdetail/desktopImageCarousel.scss';

const { desktopBaseUrl } = marketplace;

const productViewTypes = {
  'PAIR': 'both shoes',
  'TOPP': 'top',
  'BOTT': 'bottom',
  'LEFT': 'left',
  'BACK': 'back',
  'RGHT': 'right',
  'FRNT': 'front'
};

class DesktopImageCarousel extends Component {
  state = {
    currentSlide: 0
  };

  componentDidUpdate(prevProps) {
    const { style } = this.props;
    const { style: prevStyle } = prevProps;
    if (prevStyle !== style) {
      const { currentSlide } = this.state;
      const images = this.getMsaImages(style);
      if (currentSlide === 'video' || typeof images[currentSlide] === 'undefined') {
        this.setState({ currentSlide: 0 });
      }
    }
  }

  onSlideChange = (slideIndex, event) => {
    event.preventDefault();
    this.setState({ currentSlide: slideIndex });
  };

  getMsaImages = style => style.images;

  handleThumbnailClick = event => {
    event.preventDefault();
    event.stopPropagation();
    const { currentSlide } = this.state;
    const { onThumbnailClick } = this.props;
    const index = parseInt(event.currentTarget.getAttribute('data-index'), 10);
    const isImageLoading = currentSlide !== index;
    this.setState({ currentSlide: index, isImageLoading });
    onThumbnailClick();
  };

  makeAccessLabel(isSelected, type, index) {
    const productView = productViewTypes[type] || index;
    if (isSelected) {
      return `Active product view, ${productView}`;
    } else {
      return `Toggle product view to ${productView}`;
    }
  }

  makeThumbnailImg = ({ image, imageNumber, totalImages }) => {
    const { imageId, type } = image;
    const src = constructMSAImageUrl(imageId, { width: 106, height: 78 });
    const a11yTranslations = {
      MAIN: 'Main View',
      PAIR: 'Pair View',
      TOPP: 'Top View',
      BOTT: 'Bottom View',
      LEFT: 'Left View',
      BACK: 'Back View',
      RGHT: 'Right View',
      FRNT: 'Front View'
    };
    const loaderProps = {
      imgProps: { alt: a11yTranslations[type] || `View #${imageNumber} of #${totalImages}`, src },
      placeholder: <div className={css.thumbnailPlaceholder} />
    };
    return <ImageLazyLoader {...loaderProps} />;
  };

  makeVideoPlayer = (video, slotDetails, isYouTubeVideo) => (
    <div className={css.productVideoContainer}>
      <MelodyVideoPlayer
        slotDetails={slotDetails}
        isYouTubeVideo={isYouTubeVideo}
        heightValue={'270px'} />
    </div>
  );

  render() {
    const {
      context: { testId },
      props: {
        isSpotlightActive, isYouTubeVideo, makeSpotlight, onProductImageClick,
        onStyleChange, productStyles, productType, style, styleThumbnails,
        videos, youtubeSrc
      },
      state: { currentSlide }
    } = this;
    const { productId } = style;
    const msaImages = this.getMsaImages(style);
    const video = videos && videos.length ? videos.find(video => video.videoEncodingExtension === 'mp4') : null;
    const videoSrcUrl = isYouTubeVideo ? youtubeSrc : video && video.filename ? `${desktopBaseUrl}${video.filename}` : null;
    const slotDetails = {
      src: videoSrcUrl,
      productId,
      componentName: DESKTOP_PDP_VIDEO,
      autoplay: true
    };
    const msaNumberOfImages = msaImages.length;
    return (
      <div className={css.imageCarousel}>
        <div id="thumbnailsList" className={css.thumbnailsList}>
          {msaImages.map((image, index) => (
            <li key={`${image.imageId}-${index}`}>
              <button
                type="button"
                ref={`thumbnail-${index}`}
                data-index={index}
                className={cn(css.thumbnail, { [css.active]: currentSlide === index })}
                onClick={this.handleThumbnailClick}
                data-test-id={testId('desktopImageThumbnail')}
                aria-label={this.makeAccessLabel(currentSlide === index, msaImages[index].type, index)}>
                {this.makeThumbnailImg({ image, imageNumber: index + 1, totalImages: msaNumberOfImages })}
              </button>
            </li>
          ))}
          {video && (
            <li>
              <button
                type="button"
                className={cn(css.videoThumbnail, { [css.active]: currentSlide === 'video' })}
                onClick={this.onSlideChange.bind(null, 'video')}
                data-test-id={testId('desktopVideoThumbnail')}
                aria-label={currentSlide === 'video' ? 'Product video is active' : 'View product video'}>
                <span className={css.videoIcon}>
                  <VideoIcon/> Video
                </span>
              </button>
            </li>
          )}
        </div>
        <div className={css.productImageContainer}>
          <div className={css.productImage}>
            <div className={css.slider}>
              {currentSlide !== 'video' && msaImages[currentSlide] && (
                <ImageZoom
                  lowResImageSrc={constructMSAImageUrl(msaImages[currentSlide].imageId, { width: 700 })}
                  hiResImageSrc={constructMSAImageUrl(msaImages[currentSlide].imageId)}
                  altText={msaImages[currentSlide].type}
                  onProductImageClick={onProductImageClick}
                  isSpotlightActive={isSpotlightActive}/>
              )}
              {(video && currentSlide === 'video') ? this.makeVideoPlayer(video, slotDetails, isYouTubeVideo) : null}
            </div>
            {makeSpotlight()}
          </div>
          <div id="desktopSwatchPicker">
            {
              !ProductUtils.isGiftCard(productType) &&
                <DesktopSwatchPicker
                  productStyles={productStyles}
                  selectedStyleId={style.styleId}
                  onStyleChange={onStyleChange}
                  thumbnails={styleThumbnails}/>
            }
          </div>

        </div>
      </div>
    );
  }
}

DesktopImageCarousel.propTypes = {
  videos: PropTypes.array,
  productStyles: PropTypes.object.isRequired,
  productType: PropTypes.string.isRequired,
  style: PropTypes.object.isRequired,
  onStyleChange: PropTypes.func.isRequired
};

DesktopImageCarousel.contextTypes = {
  testId: PropTypes.func
};

export default DesktopImageCarousel;
